import React, { useState, useEffect, useRef } from 'react';
import { Player } from '../../../types';

interface PlayerActionsProps {
  player: Player;
  userIsCreator: boolean;
  currentUserId?: string;
  matchStatus?: string;
  isMatchCreator?: boolean;
  isMobile: boolean;
  isCompact: boolean;
  onRemovePlayer: (playerId: string) => void;
  onSelfRemove?: (playerId: string) => void;
  onEditPlayer?: (player: Player) => void;
  onCreateReview?: (player: Player) => void;
  onViewReviews?: (player: Player) => void;
  onValidateStats?: (player: Player) => void;
  hasReviewedPlayer?: boolean;
  canViewReviews?: boolean;
  loadingReviews?: boolean;
  hasPendingStats?: boolean;
  onGenerateReplacementLink?: () => void;
}

// ── Desktop gear menu ───────────────────────────────────────────────────────
const menuItemStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#fff',
  fontSize: 12,
  fontWeight: 500,
  padding: '6px 10px',
  cursor: 'pointer',
  textAlign: 'left',
  borderRadius: 6,
  transition: 'background 0.15s',
  width: '100%',
  fontFamily: 'inherit',
};

const GearMenu: React.FC<{
  canRemove: boolean;
  canEdit: boolean;
  canSelfRemove: boolean;
  canGenerateLink: boolean;
  onRemove: () => void;
  onEdit?: () => void;
  onSelfRemove?: () => void;
  onGenerateLink?: () => void;
}> = ({ canRemove, canEdit, canSelfRemove, canGenerateLink, onRemove, onEdit, onSelfRemove, onGenerateLink }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', marginLeft: '8px', flexShrink: 0 }}>
      <button
        data-testid="player-gear-btn"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        style={{
          background: open ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.07)',
          border: 'none',
          borderRadius: '50%',
          width: '26px',
          height: '26px',
          color: '#aaa',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '13px',
          padding: 0,
          transition: 'all 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#fff'; }}
        onMouseLeave={(e) => { if (!open) { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = '#aaa'; } }}
        title="Acciones"
      >
        &#9881;
      </button>
      {open && (
        <div
          data-testid="player-action-menu"
          style={{
            position: 'absolute',
            bottom: '100%',
            right: 0,
            marginBottom: 4,
            background: 'rgba(30,30,30,0.95)',
            backdropFilter: 'blur(8px)',
            borderRadius: 10,
            padding: 6,
            zIndex: 30,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            border: '1px solid rgba(255,255,255,0.15)',
            minWidth: 150,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {canEdit && onEdit && (
            <button
              onClick={() => { onEdit(); setOpen(false); }}
              style={{ ...menuItemStyle, color: '#4CAF50' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              ✏ Editar
            </button>
          )}
          {canRemove && (
            <button
              onClick={() => { onRemove(); setOpen(false); }}
              style={{ ...menuItemStyle, color: '#F44336' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              ✕ Eliminar jugador
            </button>
          )}
          {canSelfRemove && onSelfRemove && (
            <button
              onClick={() => { onSelfRemove(); setOpen(false); }}
              style={{ ...menuItemStyle, color: '#F44336' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              ↩ Darme de baja
            </button>
          )}
          {canGenerateLink && onGenerateLink && (
            <button
              type="button"
              style={menuItemStyle}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'none'; }}
              onClick={() => { onGenerateLink(); setOpen(false); }}
            >
              🔗 Generar link de reemplazo
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const PlayerActions: React.FC<PlayerActionsProps> = ({
  player,
  userIsCreator,
  currentUserId,
  matchStatus,
  isMatchCreator,
  isMobile,
  isCompact,
  onRemovePlayer,
  onSelfRemove,
  onEditPlayer,
  onCreateReview,
  onViewReviews,
  onValidateStats,
  hasReviewedPlayer,
  canViewReviews,
  loadingReviews,
  hasPendingStats,
  onGenerateReplacementLink,
}) => {
  const isDesktop = !isMobile && !isCompact;
  const isFinished = matchStatus === 'FINISHED';
  const isOwnPlayer = currentUserId && player.userId === currentUserId;
  const canRemove = userIsCreator && matchStatus !== 'FINISHED' && !isOwnPlayer;
  const canEdit = (isOwnPlayer || userIsCreator) && matchStatus !== 'FINISHED';
  const canSelfRemove = isOwnPlayer && !isMatchCreator && matchStatus !== 'FINISHED';
  const canGenerateLink = (userIsCreator || !!isOwnPlayer) && matchStatus !== 'FINISHED' && matchStatus !== 'CANCELLED';

  // Botones para mobile/compact
  if (isMobile || isCompact) {
    const buttons = [];
    
    // Botón eliminar (solo para creador, no para el propio jugador)
    if (canRemove) {
      buttons.push(
        <button
          key="remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemovePlayer(player.id);
          }}
          style={{
            background: '#FF1744',
            border: 'none',
            borderRadius: '8px',
            padding: '4px 8px',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 'bold',
            marginLeft: buttons.length > 0 ? '4px' : 'auto',
            marginTop: '4px',
            flexShrink: 0,
            fontFamily: 'inherit',
            position: 'relative',
            zIndex: 10
          }}
          title="Eliminar jugador"
        >
          Eliminar
        </button>
      );
    }
    
    // Botón editar (para el propio jugador o creador)
    if (canEdit && onEditPlayer) {
      buttons.push(
        <button
          key="edit"
          onClick={(e) => {
            e.stopPropagation();
            onEditPlayer(player);
          }}
          style={{
            background: '#4CAF50',
            border: 'none',
            borderRadius: '8px',
            padding: '4px 8px',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 'bold',
            marginLeft: buttons.length > 0 ? '4px' : 'auto',
            marginTop: '4px',
            flexShrink: 0,
            fontFamily: 'inherit',
            position: 'relative',
            zIndex: 10
          }}
          title="Editar posición y valoración"
        >
          Editar
        </button>
      );
    }
    
    // Botón darme de baja (solo para el propio jugador, no creador)
    if (canSelfRemove && onSelfRemove) {
      buttons.push(
        <button
          key="self-remove"
          onClick={(e) => {
            e.stopPropagation();
            onSelfRemove(player.id);
          }}
          style={{
            background: '#FF1744',
            border: 'none',
            borderRadius: '8px',
            padding: '4px 8px',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 'bold',
            marginLeft: buttons.length > 0 ? '4px' : 'auto',
            marginTop: '4px',
            flexShrink: 0,
            fontFamily: 'inherit',
            position: 'relative',
            zIndex: 10
          }}
          title="Darme de baja del partido"
        >
          Darme de baja
        </button>
      );
    }

    if (canGenerateLink && onGenerateReplacementLink) {
      buttons.push(
        <button
          key="replacement-link"
          onClick={(e) => {
            e.stopPropagation();
            onGenerateReplacementLink();
          }}
          style={{
            background: '#1565C0',
            border: 'none',
            borderRadius: '8px',
            padding: '4px 8px',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 'bold',
            marginLeft: buttons.length > 0 ? '4px' : 'auto',
            marginTop: '4px',
            flexShrink: 0,
            fontFamily: 'inherit',
            position: 'relative',
            zIndex: 10
          }}
          title="Generar link de reemplazo"
        >
          🔗 Link
        </button>
      );
    }

    // Si hay botones, mostrarlos en un contenedor
    if (buttons.length > 0) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          marginLeft: 'auto',
          marginTop: '4px'
        }}>
          {buttons}
        </div>
      );
    }
    
    return null;
  }

  // Botones para desktop — single gear button with dropdown menu
  if (isDesktop) {
    const hasActions = canRemove || (canEdit && onEditPlayer) || (canSelfRemove && onSelfRemove) || (canGenerateLink && onGenerateReplacementLink);
    if (!hasActions) return null;

    return <GearMenu
      canRemove={canRemove}
      canEdit={!!(canEdit && onEditPlayer)}
      canSelfRemove={!!(canSelfRemove && onSelfRemove)}
      canGenerateLink={canGenerateLink}
      onRemove={() => onRemovePlayer(player.id)}
      onEdit={onEditPlayer ? () => onEditPlayer(player) : undefined}
      onSelfRemove={onSelfRemove ? () => onSelfRemove(player.id) : undefined}
      onGenerateLink={canGenerateLink ? onGenerateReplacementLink : undefined}
    />;
  }

  return null;
};
